import { useFormikContext } from 'formik'
import { values as _values } from 'lodash'
import { ChangeEvent, ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { INewPlaceForm } from 'features/Places/types'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { Input } from 'components/Input'
import { InputTags } from 'components/v2/InputTags'
import { SelectTags } from 'components/v2/SelectTags'
import {
  GTBusinessClientAutocomplete,
  GTCompanyV2Vertical,
  GTPlaceAmenity,
  GTTPlaceVertical
} from 'gql/types.generated'
import { SelectPlaceType } from 'features/Places/components/SelectPlaceType'
import { SelectAreaSuggestion } from 'features/Places/components/SelectAreaSuggestion'
import { getParamValue, mapAmenitiesToForm } from 'features/Places/utils'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { Label } from 'components/v2/Label'
import { Radio, RadioWrapper } from 'components/v2/Radio'
import { safeFormatMessage } from '@10xlabs/utils'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { CompanySuggestion, CompanyV2Vertical } from 'gql/generated/graphql'

const VERTICALS_LIST = [GTTPlaceVertical.Travel, GTTPlaceVertical.Team, GTTPlaceVertical.Rental]
const verticalMessages = {
  [GTTPlaceVertical.Travel]: {
    id: 'places.new.form.vertical.travel',
    defaultMessage: 'Travel'
  },
  [GTTPlaceVertical.City]: {
    id: 'places.new.form.vertical.city',
    defaultMessage: 'City'
  },
  [GTTPlaceVertical.Rental]: {
    id: 'places.new.form.vertical.rental',
    defaultMessage: 'Rentals'
  },
  [GTTPlaceVertical.Team]: {
    id: 'places.new.form.vertical.team',
    defaultMessage: 'Teams'
  }
}

const messages = defineMessages({
  companyPlaceholder: {
    id: 'places.new.form.company.placeholder',
    defaultMessage: 'Select a company'
  },
  namePlaceholder: {
    id: 'places.new.form.name.placeholder',
    defaultMessage: 'Name'
  },
  paramPlaceholder: {
    id: 'places.new.form.param.placeholder',
    defaultMessage: 'Alias for the route url'
  },
  codePlaceholder: {
    id: 'places.new.form.code.placeholder',
    defaultMessage: 'Code for segments autocomplete'
  },
  areaPlaceholder: {
    id: 'places.new.form.area.placeholder',
    defaultMessage: "Place's area"
  },
  tagsPlaceholder: {
    id: 'place.new.form.tags.placeholder',
    defaultMessage: 'Press Enter to add tag'
  },
  selectAmenitiesPlaceholder: {
    id: 'places.new.form.selectAmenities.placeholder',
    defaultMessage: 'Select amenities'
  }
})

export const GeneralInformationFormCard = ({ isEdit }: { isEdit?: boolean }): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, touched, errors, handleBlur } = useFormikContext<INewPlaceForm>()

  const handleNameChange = async ({ target: { value } }: ChangeEvent<HTMLInputElement>): Promise<void> => {
    await setFieldValue('name', value)

    setFieldValue('param', getParamValue(value, values.city))
  }

  const handleTagsChange = (values: string[]): void => {
    setFieldValue('tags', values)
  }

  const amenitiesOptions = mapAmenitiesToForm(_values(GTPlaceAmenity), intl)

  const filteredAmenitiesOptions = amenitiesOptions.filter((amenitie) => {
    return !values.amenities?.find((item) => item.id === amenitie.id)
  })

  const handleVerticalChange = async ({
    target: { id: value }
  }: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    await setFieldValue('vertical', value)
    await handleCompanyChange({ id: '', name: '', vertical: value as GTCompanyV2Vertical })
  }

  const handleCompanyChange = async (company: CompanySuggestion): Promise<void> => {
    await setFieldValue('company', company)
    await handleBusinessClientChange({ id: '', name: '', verticals: [] })
  }

  const handleBusinessClientChange = async (businessClient: GTBusinessClientAutocomplete): Promise<void> => {
    await setFieldValue('businessClient', businessClient)
    setFieldValue('businessSite', { id: '', name: '' })
  }

  const shouldSelectCompany = values.vertical === GTTPlaceVertical.Team || values.vertical === GTTPlaceVertical.Rental

  return (
    <FormLayout.Card small>
      <Heading.H4 small>
        <FormattedMessage id="places.form.section.general.title" defaultMessage="General information" />
      </Heading.H4>
      <Row noMargin>
        <Column>
          <Label>
            <FormattedMessage id="companies.new.form.vertical.label" defaultMessage="Vertical" />
          </Label>
          <RadioWrapper>
            {VERTICALS_LIST.map((vertical: GTTPlaceVertical): ReactElement => {
              return (
                <Radio
                  id={vertical}
                  key={vertical}
                  name="vertical"
                  checked={values.vertical === vertical}
                  onChange={handleVerticalChange}
                  disabled={isEdit}
                >
                  {vertical === GTTPlaceVertical.Travel ? (
                    <FormattedMessage id="places.new.form.vertical.travelCity" defaultMessage="Travel / City" />
                  ) : (
                    safeFormatMessage({
                      intl,
                      key: vertical,
                      messages: verticalMessages
                    })
                  )}
                </Radio>
              )
            })}
          </RadioWrapper>
          {touched.vertical && errors.vertical && <ErrorMessage>{errors.vertical}</ErrorMessage>}
        </Column>
        {shouldSelectCompany && (
          <Column>
            <CompanyAutocomplete
              id="company"
              onChange={handleCompanyChange}
              value={values.company?.name}
              placeholder={intl.formatMessage(messages.companyPlaceholder)}
              label={<FormattedMessage id="dictionary.company" defaultMessage="Company" />}
              disabled={isEdit}
              verticals={[values.vertical as CompanyV2Vertical]}
              touched={touched.company?.id}
              errors={errors.company?.id}
            />
          </Column>
        )}
      </Row>
      {shouldSelectCompany && (
        <Row noMargin>
          <Column>
            <BusinessClientAutocomplete
              id="businessClient"
              companyId={values.company?.id}
              onChange={handleBusinessClientChange}
              value={values.businessClient?.name}
              placeholder="Business client"
              label={<FormattedMessage id="dictionary.businessClient" defaultMessage="Business client" />}
              disabled={isEdit}
              touched={touched.businessClient?.id}
              errors={errors.businessClient?.id}
            />
          </Column>
          <Column>
            <BusinessSiteAutocomplete
              id="businessSite"
              businessClientId={values.businessClient?.id}
              onChange={(businessSite): void => {
                setFieldValue('businessSite', businessSite)
              }}
              value={values.businessSite?.name}
              placeholder="Business site"
              label={<FormattedMessage id="dictionary.businessSite" defaultMessage="Business site" />}
              disabled={isEdit}
            />
          </Column>
        </Row>
      )}
      <Row noMargin>
        <Column width="50%">
          <Input
            id="name"
            onChange={handleNameChange}
            onBlur={handleBlur}
            value={values.name}
            placeholder={intl.formatMessage(messages.namePlaceholder)}
            touched={touched.name}
            errors={errors.name}
            label={<FormattedMessage id="dictionary.name" defaultMessage="Name" />}
          />
        </Column>
        <Column>
          <Input
            id="param"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.param}
            placeholder={intl.formatMessage(messages.paramPlaceholder)}
            touched={touched.param}
            errors={errors.param}
            label={<FormattedMessage id="dictionary.param" defaultMessage="Param" />}
          />
        </Column>
        <Column>
          <Input
            id="code"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.code}
            placeholder={intl.formatMessage(messages.codePlaceholder)}
            touched={touched.code}
            errors={errors.code}
            label={<FormattedMessage id="dictionary.code" defaultMessage="Code" />}
            readOnly={isEdit}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <SelectPlaceType
            id="type"
            value={values.type}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.type}
            errors={errors.type}
            label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
          />
        </Column>
        <Column>
          <SelectAreaSuggestion
            id="area"
            onChange={(area: TSFixMe): TSFixMe => setFieldValue('area', area)}
            onBlur={handleBlur}
            value={values.area}
            placeholder={intl.formatMessage(messages.areaPlaceholder)}
            label={
              <FormattedMessage
                id="dictionary.area"
                defaultMessage="Area <small>Optional</small>"
                values={{
                  small: (msg) => <small>{msg}</small>
                }}
              />
            }
          />
        </Column>
        <Column>
          <InputTags
            id="tags"
            values={values.tags}
            onChange={handleTagsChange}
            onBlur={handleBlur}
            placeholder={intl.formatMessage(messages.tagsPlaceholder)}
            label={
              <FormattedMessage
                id="places.new.form.tags.label"
                defaultMessage="Tags <small>Optional</small>"
                values={{
                  small: (msg) => <small>{msg}</small>
                }}
              />
            }
          />
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <SelectTags
            placeholder={intl.formatMessage(messages.selectAmenitiesPlaceholder)}
            values={values.amenities}
            label={
              <FormattedMessage
                id="places.new.form.longitude.label"
                defaultMessage="Amenities <small>(Optional)</small>"
                values={{
                  small: (msg) => <small>{msg}</small>
                }}
              />
            }
            items={filteredAmenitiesOptions}
            getTagLabel={(amenitie): string => amenitie.label}
            onChange={(values: { id: string; label: string }[]): void => {
              setFieldValue('amenities', values)
            }}
          />
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
